import { cn } from "../utils";
import { INTENT_CLASSES, type Intent } from "../tokens/intents";
import { Sparkline } from "./sparkline";

function resolveChartColor(accentColor: string | undefined): string {
  // Sparklines lead with indigo (the brand chart color). `var(--foreground)`
  // is intentionally remapped — a black sparkline reads as broken, not neutral.
  // `var(--viz-sky)` is the same indigo as `--chart-1`; normalize to the chart
  // token so every sparkline sources from the one palette.
  if (
    !accentColor ||
    accentColor === "var(--foreground)" ||
    accentColor === "var(--viz-sky)"
  ) {
    return "var(--chart-1)";
  }
  return accentColor;
}

/**
 * One row of the structured footer (e.g. "● 2 In progress · ● 13 Todo").
 * Use `intent` for status counts that map to the intent registry; omit
 * `intent` for items that should render at reduced opacity (no semantic
 * color signal).
 */
export type StatFooterItem = {
  count: number | string;
  /** Sentence-case label — pass `STATUS_LABEL[status]` for task statuses. */
  label: string;
  intent?: Intent;
};

export interface StatCardProps {
  title?: string;
  value: string;
  description?: string;
  /** % change vs previous period. Positive = up, negative = down. */
  trend?: number;
  /**
   * Which direction of `trend` is "good". `"up"` colors positive trends
   * green and negative trends red (e.g. revenue, shipped count).
   * `"down"` flips it — for metrics where lower = better (open backlog,
   * stalled tasks). Defaults to `"none"` (no trend coloring).
   */
  trendGood?: "up" | "down" | "none";
  /**
   * Timeframe suffix for the trend (e.g. `"vs prev 7d"`). Rendered in
   * muted text after the percent — `"+181.3% vs prev 7d"`. A naked
   * percent is hard to interpret; callers that pass `trend` should
   * almost always pass this too.
   */
  trendLabel?: string;
  /** Raw values for the inline sparkline. */
  sparkData?: number[];
  /** Sparkline color only — text is always foreground. */
  accentColor?: string;
  /**
   * Structured status footer. When present, takes precedence over
   * `description`. Each item renders a colored dot from the intent
   * registry; items without an intent render at reduced opacity.
   */
  footer?: StatFooterItem[];
  /**
   * Optional status dot rendered immediately before the title. Used by
   * `<StatStrip variant="board">` to express the status pipeline (a leading
   * cue per stage). Resolves through the intent registry like every other dot.
   */
  leadingDot?: Intent;
  /**
   * Toggles hover affordance. Set when the card is wrapped in a link
   * so users can tell it's clickable. Renders a subtle bg shift only —
   * no border or transform.
   */
  interactive?: boolean;
}

export function StatCard({
  title,
  value,
  description,
  trend,
  trendGood = "none",
  trendLabel,
  sparkData,
  accentColor,
  footer,
  leadingDot,
  interactive,
}: StatCardProps) {
  const resolvedAccent = resolveChartColor(accentColor);
  const hasTrend = trend !== undefined && !isNaN(trend);
  const isUp = hasTrend && trend! >= 0;
  const rawSpark = sparkData ?? [];
  const trendIsGood =
    !hasTrend || trendGood === "none" || trend === 0
      ? null
      : trendGood === "up"
        ? isUp
        : !isUp;
  const trendClass =
    trendIsGood === null
      ? "text-muted-foreground"
      : trendIsGood
        ? "text-success"
        : "text-destructive";

  const hasFooter = footer && footer.length > 0;

  return (
    // Minimal design language (docs/design/MINIMAL-SURFACES-PRD.md): a BARE
    // metric — the number is the loudest thing, not a boxed tile. Muted label
    // above, oversized tabular value, inline ±delta. No card, no shadow.
    <div
      className={cn(
        // gap-2.5 (10px) between label · value · support. Even + exact, because
        // ALL THREE lines are cap-trimmed (title/footer above + the value below),
        // so the flex gap IS the optical gap — not an approximate value inflated
        // unevenly by the value's leading-display line-box vs the trimmed neighbors.
        "flex flex-col gap-2.5",
        interactive &&
          "cursor-pointer transition-opacity hover:opacity-70",
      )}
    >
      {title && (
        // leading-ui + cap-trim: the single-line label sits flush to its cap
        // height, so the cell's top padding measures to the cap — not to the
        // phantom half-leading of a 20px line-box over 13px text. Pairs with the
        // footer/description trim below to make py-4 top == py-4 bottom optically.
        <span className="flex items-center gap-2 type-body leading-ui [text-box:trim-both_cap_alphabetic] text-muted-foreground">
          {leadingDot && (
            <span
              aria-hidden
              className={cn("size-2 rounded-full", INTENT_CLASSES[leadingDot].dot)}
            />
          )}
          {title}
        </span>
      )}

      <div className="flex items-baseline gap-2">
        <span className="type-heading font-medium tabular-nums leading-display [text-box:trim-both_cap_alphabetic] text-foreground">
          {value}
        </span>
        {hasTrend && (
          <span className={cn("type-body tabular-nums", trendClass)}>
            {isUp ? "+" : ""}
            {trend!.toFixed(1)}%
          </span>
        )}
      </div>

      {hasTrend && trendLabel && (
        <span className="type-subtext text-muted-foreground">{trendLabel}</span>
      )}

      {rawSpark.length > 0 && (
        <Sparkline values={rawSpark} color={resolvedAccent} />
      )}

      {hasFooter ? (
        // The support line (this footer / the description below) is NOT
        // cap-trimmed: text-box-trim only trims a block's line-boxes and is a
        // no-op on a flex row like this footer. Trimming the block <description>
        // but not this flex footer left the value→support gap inconsistent
        // between cells — so BOTH keep their natural type-subtext leading, one gap.
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 type-subtext text-muted-foreground">
          {footer!.map((item, i) => {
            const muted = !item.intent;
            return (
              <span
                key={i}
                className={cn(
                  "inline-flex items-center gap-1.5",
                  muted && "opacity-60",
                )}
              >
                {item.intent && (
                  <span
                    aria-hidden
                    className={cn(
                      "size-2 rounded-full",
                      INTENT_CLASSES[item.intent].dot,
                    )}
                  />
                )}
                <span className="tabular-nums">{item.count}</span>
                <span>{item.label}</span>
                {i < footer!.length - 1 && (
                  <span className="text-muted-foreground/40 ml-1">·</span>
                )}
              </span>
            );
          })}
        </div>
      ) : (
        description && (
          <div className="type-subtext text-muted-foreground">{description}</div>
        )
      )}
    </div>
  );
}
